import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import NotificationsDropdown from './NotificationsDropdown';
import {
  LayoutDashboard,
  FileText,
  Building2,
  PackageOpen,
  Users,
  Settings,
  LogOut,
  BarChart3,
  CreditCard,
  Megaphone,
  Award,
  Gift,
  ShoppingBag,
  MessageSquare,
  Trophy,
  Star,
  Lock,
  UserCheck,
} from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = {
  PROVIDER: [
    { icon: Star, label: 'Fidelización', path: '/loyalty' },
    { icon: Trophy, label: 'Ranking', path: '/ranking' },
    { icon: FileText, label: 'Extravisibilidad', path: '/requests', divider: true },
    { icon: CreditCard, label: 'Credenciales', path: '/credentials' },
    { icon: Megaphone, label: 'Impulsaciones', path: '/promotions' },
  ],
  EXECUTIVE: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: FileText, label: 'Solicitudes', path: '/requests' },
    { icon: CreditCard, label: 'Credenciales', path: '/credentials' },
    { icon: Megaphone, label: 'Impulsaciones', path: '/promotions' },
    { icon: BarChart3, label: 'Inventario', path: '/inventory' },
  ],
  ADMIN: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: FileText, label: 'Extravisibilidad', path: '/requests' },
    { icon: CreditCard, label: 'Credenciales', path: '/credentials' },
    { icon: Megaphone, label: 'Impulsaciones', path: '/promotions' },
    { icon: BarChart3, label: 'Inventario', path: '/inventory' },
    { icon: Lock, label: 'Bloqueos', path: '/availability-blocks' },
    { icon: Building2, label: 'Tiendas', path: '/stores' },
    { icon: UserCheck, label: 'Dependientes', path: '/store-dependents' },
    { icon: PackageOpen, label: 'Productos', path: '/space-types' },
    { icon: Users, label: 'Proveedores', path: '/providers' },
    { icon: Award, label: 'Insignias', path: '/badges', divider: true },
    { icon: Gift, label: 'Beneficios', path: '/benefits' },
    { icon: ShoppingBag, label: 'Canjes', path: '/redemptions' },
    { icon: MessageSquare, label: 'Comunicados', path: '/communications' },
    { icon: Trophy, label: 'Ranking', path: '/ranking' },
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = React.useState(window.location.pathname);

  const userNavItems = navItems[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setCurrentPath(path);
  };

  React.useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 flex flex-col z-50">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_ad-credential-hub/artifacts/auhn0hjo_image.png" 
              alt="Farmaenlace" 
              className="h-12 object-contain"
            />
          </div>
          <p className="text-xs text-slate-500 text-center mt-2">{user?.name}</p>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {userNavItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = currentPath === item.path;
            return (
              <React.Fragment key={item.path}>
                {item.divider && (
                  <div className="pt-3 pb-2">
                    <div className="border-t border-slate-200" />
                    <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mt-3 px-4">Fidelización</p>
                  </div>
                )}
                <button
                  onClick={() => handleNavigation(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="mb-3 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-1">Rol</p>
            <p className="text-sm font-semibold text-slate-900">
              {user?.role === 'ADMIN' ? 'Administrador' : user?.role === 'EXECUTIVE' ? 'Ejecutivo' : 'Proveedor'}
            </p>
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen">
        {/* Top Header with Notifications */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200 px-8 py-3 flex items-center justify-end">
          <NotificationsDropdown />
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}